import {
  Grid,
  Input,
  InputVariant,
  Paper,
  Text,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import React from 'react'
import { getLanguageLabel } from '../../../utils/HelperFunctions'
import { useSaveTranslation } from '../hooks/useSaveTranslation'
import { dispatchAudienceFieldSave } from '../lib/dispatchFieldSave'
import {
  AudienceTranslatableField,
  CustomAudienceTranslationGroup
} from '../lib/extractAudienceFields'
import { useFieldDraft } from './useFieldDraft'

const upperLanguageName = (code: string): string =>
  getLanguageLabel(code).toUpperCase()

interface FieldRowProps {
  field: AudienceTranslatableField
  targetLanguage: string
  onSave: (text: string) => Promise<unknown> | undefined
  isFirst: boolean
}

const FieldRow: React.FC<FieldRowProps> = ({
  field,
  targetLanguage,
  onSave,
  isFirst
}) => {
  const { draft, setDraft, persist } = useFieldDraft(
    field,
    targetLanguage,
    onSave
  )

  return (
    <Grid
      container
      direction="row"
      spacing={2}
      alignItems="flex-start"
      style={{ marginTop: isFirst ? 0 : 12 }}
    >
      <Grid item xs={6}>
        {/* Matches the input opposite it — see EntryTranslationCard. */}
        <Text
          size={TextSize.ms}
          weight={isFirst ? TextWeight.SemiBold : TextWeight.Regular}
        >
          {field.master || ' '}
        </Text>
      </Grid>
      <Grid item xs={6}>
        <Input
          multiline
          fullWidth
          variant={InputVariant.Outlined}
          ariaLabel={`Audience ${field.label} in ${targetLanguage}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={persist}
        />
      </Grid>
    </Grid>
  )
}

interface Props {
  group: CustomAudienceTranslationGroup
  targetLanguage: string
  primaryLanguage: string
}

const AudienceTranslationCard: React.FC<Props> = ({
  group,
  targetLanguage,
  primaryLanguage
}) => {
  const save = useSaveTranslation()
  if (group.fields.length === 0) return null
  const positionLabel = `A${group.position}`

  const handleSave = (field: AudienceTranslatableField, text: string) =>
    dispatchAudienceFieldSave(save, field, text, targetLanguage)

  return (
    <Paper>
      <div style={{ padding: 16 }}>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={6}>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              {upperLanguageName(primaryLanguage)} ({positionLabel})
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              {upperLanguageName(targetLanguage)}
            </Text>
          </Grid>
        </Grid>
        <div style={{ marginTop: 8 }}>
          {group.fields.map((field, idx) => (
            <FieldRow
              key={field.key}
              field={field}
              targetLanguage={targetLanguage}
              onSave={(text) => handleSave(field, text)}
              isFirst={idx === 0}
            />
          ))}
        </div>
      </div>
    </Paper>
  )
}

export default AudienceTranslationCard
